import { Node } from '@policygenius/dif-ts-types';
import formatResponseError from 'api/util/formatResponseError';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { addGlobalError } from 'containers/App/actions';
import getDataTypeFromNode from 'util/interviews/getDataTypeFromNode';

import {
  DatapointvaluesDPIDValueItem,
  DatapointvaluesErrorResponse,
  DatapointvaluesGetResponse,
} from '../../';
import useApi, { ApiArgs } from '../useApi';

export interface UseGetDataPointValuesArgs extends ApiArgs<DatapointvaluesGetResponse> {
  /**
   * By default, useGetDataPointValues will make a request on mount of the hook.
   * Use `lazy: true` to disable that functionality and make the API request manually.
   */
  lazy?: boolean;
  params: {
    urns: string[];
  };
}

export function useGetDataPointValues({
  lazy = false,
  params,
  onCompleted,
  onError,
}: UseGetDataPointValuesArgs) {
  const dispatch = useDispatch();
  const defaultOnError = () => dispatch(addGlobalError());

  const [api, { updateRequest, data, loading, error, activeUserUuid, referenceID }] =
    useApi<DatapointvaluesGetResponse>({
      onCompleted,
      onError: onError ?? defaultOnError,
      initialLoading: !lazy,
    });

  const get = async (): Promise<{
    data: DatapointvaluesGetResponse | null;
    error: DatapointvaluesErrorResponse | null;
  }> => {
    const { urns } = params;

    updateRequest({ status: 'initiate' });

    try {
      const { data: responseData } = await api.dataPointValues.dataPointValuesDetail(
        activeUserUuid,
        /*
          Note: We need to provide the list of urns as a comma-separated-list in a 1 item array
          in order for the request query params to be formatted properly.
          This is because a query param array will be output as a multipart param (i.e. "urns=urn-1&urn-2"),
          but our API requires a comma-separated-list (i.e. "urns=urn-1,urn-2").
          This format allows for the `urns` query param to be properly formatted for our API.
        */
        { urns: [urns.join(',')], referenceID }
      );

      updateRequest({ status: 'complete', payload: responseData });

      return {
        data: responseData,
        error: null,
      };
    } catch (e) {
      const responseError = formatResponseError(e);

      updateRequest({ status: 'error', payload: responseError });

      return {
        data: null,
        error: responseError,
      };
    }
  };

  useEffect(() => {
    if (!lazy) {
      get();
    }
  }, []);

  return {
    data,
    loading,
    error,
    getDataPointValues: get,
  };
}

export function useCreateDataPointValues({
  onCompleted,
  onError,
}: ApiArgs<DatapointvaluesDPIDValueItem[]> = {}) {
  const dispatch = useDispatch();
  const defaultOnError = () => dispatch(addGlobalError());

  const [api, { updateRequest, loading, error, activeUserUuid, referenceID }] = useApi<
    DatapointvaluesDPIDValueItem[]
  >({
    onCompleted,
    onError: onError ?? defaultOnError,
  });

  const create = async (
    dataPointValues: DatapointvaluesDPIDValueItem[]
  ): Promise<{
    data: DatapointvaluesDPIDValueItem[] | null;
    error: DatapointvaluesErrorResponse | null;
  }> => {
    updateRequest({ status: 'initiate' });

    try {
      await api.dataPointValues.dataPointValuesCreate(
        activeUserUuid,
        {
          dataPointValues,
          referenceID,
        },
        {
          format: 'json',
        }
      );

      updateRequest({ status: 'complete', payload: dataPointValues });

      return {
        data: dataPointValues,
        error: null,
      };
    } catch (e) {
      const responseError = formatResponseError(e);

      updateRequest({ status: 'error', payload: responseError });

      return {
        data: null,
        error: responseError,
      };
    }
  };

  async function createDataPointValuesFromInterview<FormValues extends Record<string, unknown>>(
    values: FormValues,
    schema: Node
  ): Promise<{ error: DatapointvaluesErrorResponse | null }> {
    const dataPointValues = buildBodyFromInterview<FormValues>(values, schema);

    return create(dataPointValues);
  }

  return {
    loading,
    error,
    createDataPointValues: create,
    createDataPointValuesFromInterview,
  };
}

export function buildBodyFromInterview<FormValues extends Record<string, unknown>>(
  values: FormValues,
  schema: Node
) {
  return Object.keys(values).reduce<DatapointvaluesDPIDValueItem[]>((items, urn) => {
    const dataType = getDataTypeFromNode(urn, schema);

    if (!dataType) {
      console.warn(`Error building body for createDataPointValues: No dataType found for ${urn}`);

      return items;
    }

    items.push({
      dataType,
      urn,
      value: values[urn as keyof FormValues],
    });

    return items;
  }, []);
}
